import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { editorHref } from '../../lib/editorSession'
import { useI18n } from '../../lib/i18n'

/** Por debajo de este ancho la página se dibuja a este tamaño y se escala. */
const MIN_DESIGN_WIDTH = 1280

type ProjectData = {
  pages: Array<Record<string, unknown>>
  projectTheme: unknown
  projectContent?: unknown
}

/**
 * La vista previa del asistente, pintada por el editor.
 *
 * Se abre el editor en modo vista previa (`?wizardPreview=1`) y se le manda el
 * proyecto tal como se va a crear —`composeProjectData`—: la página sale con
 * el mismo render que al publicar, con la barra y el pie de Insertar. Así lo
 * que se ve aquí es lo que se abre después en el editor.
 *
 * Es una ventana de navegador de verdad: del alto que tiene el hueco, con la
 * página desplazándose DENTRO. Antes el iframe medía lo que la página y era el
 * hueco el que se desplazaba, pero la página no podía medir menos que su
 * ventana y lo que va a pantalla completa (`100vh`) crecía con ella: cada
 * repintado la alargaba un poco y al bajar al pie se acababa en un hueco gris.
 *
 * Ocupa todo el ancho: en pantallas anchas la página se dibuja a ese ancho, y
 * sólo por debajo de 1280 px se dibuja a 1280 y se escala para que quepa.
 */
export function SitePreview({ data, language, focus = 'top', reveal = null, onSection, pageSlug, previewPage = null, onPage, onText, motionReplay = 0, minDesignWidth = MIN_DESIGN_WIDTH, initialScroll, goTo = null, onShown, onLink }: {
  data: ProjectData
  language: 'es' | 'en'
  /** La página que se enseña, por su slug; sin él, la portada. */
  pageSlug?: string | undefined
  /** Una página que no se va a crear, para verla antes de decidir (no entra en la barra). */
  previewPage?: Record<string, unknown> | null
  /** Se pulsó en la vista previa un enlace a otra página del sitio. */
  onPage?: (slug: string) => void
  /**
   * El ancho por debajo del cual la página se dibuja más ancha y se escala.
   * El modo diseño de veltica.com lo baja a 0: la página va a su tamaño real,
   * como la que se estaba viendo.
   */
  minDesignWidth?: number
  /** Dónde arranca la página, como fracción de su alto (0 = arriba). */
  initialScroll?: number | undefined
  /** Llevar la página a un punto (fracción de su alto) ya pintada; `nonce` para repetirlo. */
  goTo?: { fraction: number; nonce: number } | null
  /** Se pulsó un enlace que no es una página de la vista previa (otra web, otra página del sitio). */
  onLink?: ((href: string) => void) | undefined
  /** La página ya se ve, la primera vez. */
  onShown?: (() => void) | undefined
  /** Se escribió en un texto de la vista previa (doble clic): su nodo y lo escrito. */
  onText?: ((elementId: string, text: string) => void) | undefined
  /**
   * Hacia dónde mira la página. En el paso del contacto baja al pie, que es
   * donde sale lo que se está añadiendo; en los demás, se queda donde se dejó.
   */
  focus?: 'top' | 'bottom'
  /**
   * Llevar la página a una sección y resaltarla (por el id de su nodo raíz).
   * Si todavía no está —la variante nueva aún se pinta—, la vista previa la
   * espera y la enseña en cuanto aparece.
   */
  reveal?: { rootId: string; nonce: number } | null
  /** La sección que se está viendo al desplazar la página. */
  onSection?: (rootId: string | null) => void
  /**
   * Cada vez que cambia, las entradas se vuelven a ver en la página, donde
   * está, sin desplazarla (el paso «Animación»).
   */
  motionReplay?: number
}) {
  const { t } = useI18n()
  const wrapRef = useRef<HTMLDivElement>(null)
  const frameRef = useRef<HTMLIFrameElement>(null)
  const [size, setSize] = useState({ width: MIN_DESIGN_WIDTH, height: 800 })
  const [ready, setReady] = useState(false)
  const [src] = useState(() => editorHref('?wizardPreview=1'))
  const editorOrigin = new URL(src).origin

  useLayoutEffect(() => {
    const element = wrapRef.current
    if (!element) return
    const update = () => setSize({ width: element.clientWidth, height: element.clientHeight })
    update()
    const observer = new ResizeObserver(update)
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  const onSectionRef = useRef(onSection)
  onSectionRef.current = onSection
  const onPageRef = useRef(onPage)
  onPageRef.current = onPage
  const onTextRef = useRef(onText)
  onTextRef.current = onText
  const onShownRef = useRef(onShown)
  onShownRef.current = onShown
  const onLinkRef = useRef(onLink)
  onLinkRef.current = onLink

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== editorOrigin || event.source !== frameRef.current?.contentWindow) return
      const message = event.data as { type?: string; rootId?: unknown; slug?: unknown; id?: unknown; text?: unknown } | null
      if (message?.type === 'veltica:wizard-preview-page' && typeof message.slug === 'string') onPageRef.current?.(message.slug)
      if (message?.type === 'veltica:wizard-preview-text' && typeof message.id === 'string' && typeof message.text === 'string') {
        onTextRef.current?.(message.id, message.text)
      }
      if (message?.type === 'veltica:wizard-preview-ready') {
        /* Antes que el primer pintado: así la página ya sale en su punto. */
        if (initialScroll !== undefined) frameRef.current?.contentWindow?.postMessage({ type: 'veltica:wizard-preview-scroll', fraction: initialScroll }, editorOrigin)
        setReady(true)
      }
      if (message?.type === 'veltica:wizard-preview-shown') onShownRef.current?.()
      if (message?.type === 'veltica:wizard-preview-link' && typeof (message as { href?: unknown }).href === 'string') onLinkRef.current?.((message as { href: string }).href)
      if (message?.type === 'veltica:wizard-preview-section') {
        onSectionRef.current?.(typeof message.rootId === 'string' ? message.rootId : null)
      }
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  // `initialScroll` sólo cuenta al arrancar.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorOrigin])

  // Los cambios del asistente llegan agrupados: la página se repinta a lo sumo cada 180 ms.
  useEffect(() => {
    if (!ready) return
    const timer = window.setTimeout(() => {
      frameRef.current?.contentWindow?.postMessage({ type: 'veltica:wizard-preview', ...data, language, focus, pageSlug, previewPage }, editorOrigin)
    }, 180)
    return () => window.clearTimeout(timer)
    // `focus` viaja aparte (abajo): cambiarlo no repinta la página.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, language, ready, editorOrigin, pageSlug, previewPage])

  /* Al cambiar de enfoque, la página se desplaza dentro de su ventana. */
  useEffect(() => {
    if (!ready) return
    frameRef.current?.contentWindow?.postMessage({ type: 'veltica:wizard-preview-focus', focus }, editorOrigin)
  }, [focus, ready, editorOrigin])

  useEffect(() => {
    if (!ready || !goTo) return
    frameRef.current?.contentWindow?.postMessage({ type: 'veltica:wizard-preview-goto', fraction: goTo.fraction }, editorOrigin)
  }, [goTo, ready, editorOrigin])

  /* A la sección que se pidió. Va aparte de los datos: no repinta la página. */
  useEffect(() => {
    if (!ready || !reveal) return
    frameRef.current?.contentWindow?.postMessage({ type: 'veltica:wizard-preview-reveal', rootId: reveal.rootId }, editorOrigin)
  }, [reveal, ready, editorOrigin])

  /* Después de los datos (180 ms): si se eligió otro estilo, la página se
     repinta con él y las entradas se reproducen al cargar. */
  useEffect(() => {
    if (!ready || !motionReplay) return
    const timer = window.setTimeout(() => {
      frameRef.current?.contentWindow?.postMessage({ type: 'veltica:wizard-preview-motion' }, editorOrigin)
    }, 220)
    return () => window.clearTimeout(timer)
  }, [motionReplay, ready, editorOrigin])

  const designWidth = Math.max(size.width, minDesignWidth)
  const scale = Math.min(1, size.width / designWidth)
  const designHeight = Math.max(1, Math.round(size.height / scale))

  return (
    <div className="cwa-preview">
      <div className="cwa-preview-stage" ref={wrapRef}>
        <div className="cwa-preview-frame-wrap" style={{ width: designWidth * scale, height: size.height }}>
          <iframe
            ref={frameRef}
            title={t('createAi.previewTitle')}
            className="cwa-preview-iframe"
            src={src}
            style={{ width: designWidth, height: designHeight, transform: `scale(${scale})`, transformOrigin: 'top left', border: 0 }}
          />
        </div>
        {!ready ? <div className="cwa-preview-loading" aria-hidden="true" /> : null}
      </div>
    </div>
  )
}
